import React, { useEffect, useState }  from 'react';
import PropTypes from 'prop-types';
import Modal from '../../../../../components/Modal';
import { useGetAllCourseListMutation } from '../../../../../features/authentication/authApiSlice';
import DropDownSelect from '../../../../../../src/components/DropDownSelect/index';
import { useGetStandardDetailsMutation, useGetStandardTreeMutation } from '../../../../../features/authentication/standardApiSlice';
import TreeSelect from '../../../../../../src/components/TreeSelect/index';
import { Button, Col, Row } from 'react-bootstrap';
import SkeletonGrid from '../../../../../components/SkeletonLoader/SkeletonGrid';
import ProgressButton from '../../../../../components/ProgressButton';
import PageSize from '../../../../../enums/pageSize';
import usePrintPdfGenerator from '../../../../../utils/PrintPdfGenerator';

const StandardTreeModal = ({
  isOpen,
  onClose,
  selectedStandardNodes,
  setSelectedStandardNodes
}) => {
  const [getAllCourses] = useGetAllCourseListMutation();
  const [getStandardTree] = useGetStandardTreeMutation();
  const [getStandards] = useGetStandardDetailsMutation();
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [selectedCourseName, setSelectedCourseName] = useState('');
  const [courses, setCourses] = useState([]);
  const [standardNodes, setStandardNodes] = useState(null);
  const [standardNodesCount, setStandardNodeCount] = useState(0);
  const [isTreeLoading, setIsTreeLoading] = useState(false);
  const [standards, setStandards] = useState([]);
  const [showStandardModal, setShowStandardModal] = useState(false);
  const [stateName, setStateName] = useState('');
  const { printPDF, isPdfGenerating } = usePrintPdfGenerator();

  useEffect(() => {
    getAllCoursesAsync();
  }, []);
  
  useEffect(() => {
    if(selectedCourseId)
      standardTreeHandler('','root');
  }, [selectedCourseId]);

  const getAllCoursesAsync = async() => {
    let courses = localStorage.getItem('courses');
    if (courses)
      courses = JSON.parse(courses);
    else
      courses = await loadCourses();
    courses = courses.filter(item=>item.id != 255);
    setCourses(courses);
    setSelectedCourseId(courses[0].id);
    setSelectedCourseName(courses[0].name);
  };

  async function loadCourses()
  {
    var courses = await getAllCourses().unwrap();
    localStorage.setItem('courses', JSON.stringify(courses));
    return courses;
  }

  const handleCourseChange = (courseId) => {
    setSelectedCourseId(courseId);
    const selectedCourse = courses.find((item) => item.id == courseId);
    if (selectedCourse)
      setSelectedCourseName(selectedCourse.name);
  };

  const standardTreeHandler = async (rootName, nodeValue, isChildAvailable, childrens, index) => {
    let response = [];
    if (isChildAvailable)
      response = childrens;
    else {
      setIsTreeLoading(true);
      const option = { courseId: selectedCourseId   };
      response = await getStandardTree(option).unwrap();
      setStandardNodes(response);
      setIsTreeLoading(false);
    }
    setTreeNodes(response, nodeValue, rootName, standardNodes, setStandardNodes, setStandardNodeCount, index);
  };

  const setTreeNodes = async (response, nodeValue, rootName, nodes, setNodes, setNodeCount, treeIndex) => {
    try {
      if (nodeValue == 'root') {
        setNodes(response);
        setNodeCount(response.length);
      } else {
        const newIds = response.map((newItem) => newItem.id);
        if (
          !nodes[treeIndex]
            .find((i) => i.name == rootName)
            .childrens.some((item2) => item2.id == newIds[0])
        ) {
          var selectedTree = nodes[treeIndex];
          const updatedTree = selectedTree.map((item) => {
            if (item.name === rootName) {
              const updatedChildrens = item.childrens.map((child) => {
                if (child.id == nodeValue) {
                  return { ...child, children: newIds };
                }
                return child;
              });
              return { ...item, childrens: [...updatedChildrens, ...response] };
            }
            return item;
          });
          let tempTree = [...nodes];
          tempTree[treeIndex] = updatedTree;
          setNodes(tempTree);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getStandardDetails = async (nodeId , showAllStandard , flatTree) =>{
    const params = new URLSearchParams();
    if(showAllStandard){
      const selectedNode = flatTree.find(item => item.id == nodeId);
      let childIds = getAllChildIds(selectedNode);
      childIds.forEach(item => params.append('numbers', item));
    }
    else{
      [nodeId].forEach(item => params.append('numbers', item));
    }
    var response = await getStandards(params.toString()).unwrap();
    setStateName(response[0].stateName);
    setStandards(response);
    setShowStandardModal(true);
  };

  const getAllChildIds = (node) => {
    let childNodes = [];
    const collectChildren = (node) => {
      if (node?.childrens?.length) {
        node.childrens.forEach((child) => {
          if(!child.isBranch)
            childNodes.push(child.id);
          collectChildren(child);
        });
      }
    };
    collectChildren(node);
    return childNodes;
  };

  const handleStandardOnClose =()=>{
    setShowStandardModal(false);
  };

  const handlePrint = async () => {
    if(isPdfGenerating) return;

    const contentWrapper = document.getElementById('print-standards');
    const htmlString = contentWrapper.innerHTML;

    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = htmlString;

    const standardHeading = tempContainer.querySelector('#standard-header');
    if (standardHeading) {
      standardHeading.classList.remove('d-none');
    }

    await printPDF('Standards', tempContainer.innerHTML, PageSize.A3);
  };

  return (
    <>
      <Modal
        contentWidth="90%"
        isOpen={isOpen}
        titleName='Browse Standards'
        onClose={onClose}
        detailHeight='500px'>
        <div className='align-items-end d-flex gap-3'>
          <div className='w-100'>
            <label>Course</label>
            <DropDownSelect
              value={courses}
              selectedId={selectedCourseId}
              onChange={(e) => handleCourseChange(Number(e.key))}
              dropdownId="courses"
              selectedName={selectedCourseName}
            />
          </div>
          <div>
            <Button variant='castleWhite' className='btn-width' disabled={selectedStandardNodes?.length <= 0} onClick={onClose}>Ok</Button>
          </div>
        </div>
        <div className="mt-4">
          {isTreeLoading ? (
            <SkeletonGrid rowCount={1} colCount={1} className="p-2" />
          ) : (
            <>
              {standardNodes != null &&
                (standardNodes.length > 0 ? (
                  <>
                    <span className='fw-500 mb-3'>Select Standard</span>
                    <Row>
                      {standardNodes.map((node,index)=>  (
                        <Col className='col-6 mt-2' key={index}>
                          <TreeSelect
                            key={index}
                            nodes={node}
                            treeNodeHandler={standardTreeHandler}
                            parentNodesCount={standardNodesCount}
                            selectedChildIds={selectedStandardNodes}
                            setSelectedChildIds={setSelectedStandardNodes}
                            treeIndex={index}
                            getStandardDetails = {getStandardDetails}
                          />
                        </Col>
                      ))} 
                    </Row>
                  </>):(
                  <div className='text-danger mt-4'>
                  This course have no standards.
                  </div>
                ))}
            </>
          )
          }
        </div>
      </Modal>
      <Modal
        isOpen={showStandardModal}
        onClose={handleStandardOnClose}
        titleName={`${stateName != 'National Standards' ? 'Standards for' : ''} ${stateName}`}
        contentWidth='60%'>
        <div id='print-standards'>
          <h3 id='standard-header' className="primary-title mb-0 d-none">{`${stateName != 'National Standards' ? 'Standards for' : ''} ${stateName}`}</h3>
          {standards.map((standard,index)=>(
            <div className='d-flex flex-column line mb-4 p-3' key={index}>
              <div className='fw-bold mb-4'>{standard.standardsGroupName} ({standard.standardNumber})</div>
              <div dangerouslySetInnerHTML={{ __html: standard.description }}></div>
            </div>
          ))}
        </div>
        <div className='d-flex gap-3 justify-content-end mt-4'>
          <ProgressButton
            isLoading={isPdfGenerating}
            onClick={handlePrint}
            loadingLabel="Generating PDF"
            label="Print to PDF"
            className="btn btn-castleRed"
          />
          <button className='btn btn-castleWhite btn-width' onClick={() => setShowStandardModal(false)}>Ok</button>
        </div>
      </Modal>
    </>
  );
};

StandardTreeModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  selectedStandardNodes: PropTypes.array,
  setSelectedStandardNodes: PropTypes.func
};

export default StandardTreeModal;
